import { useContext, useEffect, useState, useMemo, useCallback, useRef } from "react";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import { useSelector } from "react-redux";
import useRouter from "@stadium/hooks/lib/use-custom-route";

import {
  ProductListingContext,
  INIT_PRICE_RANGE_IDS,
} from "@components/store/products/listing/listing-provider";
import AddOndemandModal from "@components/modals/add-ondemand";

import { getShowOndemandModal } from "@helpers/menu";

import { useModal } from "@stadium/hooks/lib/use-modal";

import { useWithAnalytics } from "@stadium/services/analytics/hooks/use-with-analytics";
import { useShopAnalyticsContext } from "@hooks/analytics/use-shop-analytics-context";

import { useWorkspaceContext } from "@contexts/workspace-context";
import { useCreateSwagContext } from "@contexts/create-swag-context";

import { useStoreContext } from "@components/store/provider";

import { getIsBundleShop, getIsLockerShop, getIsPricelessShop } from "@selectors/store";

import { useConfig } from "@stadium/hooks/lib/use-config";
import { selectRestrictionTaxons } from "@stadium/redux-utils/selectors/configSelector";

import {
  BRAND_FILTER_KEY,
  COLOR_FILTER_KEY,
  CUSTOM_FILTERS,
  DIETARY_RESTRICTIONS_FILTER_KEY,
  COUNTRY_FILTER_KEY,
  ITEM_FEATURES_FILTER_KEY,
  PRICE_FILTER_KEY,
  PRICE_LABEL_TEXT,
  SIZE_FILTER_KEY,
  ALL_PRODUCTS_FILTER,
  LOCKER_INVENTORY_FILTER_KEY,
  SORTED_FILTER_ITEMS,
  DECORATION_METHOD_KEY,
  ITEM_TYPE_FILTER_KEY,
  ALL_DESIGNED_PRODUCTS_FILTER,
  DESIGNED_TYPE_FILTER_KEY,
  WORKSPACE_SHARING_DESIGNED_FILTERS,
  FILTER_ITEM_TYPES,
} from "@constants/products/listing";
import { ADD_ONDEMAND_MODAL } from "@constants/modal-types";
import { MY_DESIGNED_SWAG_LOCKER } from "@constants/menu";
import {
  ANALYTICS_EVENT_ACTION_APPLIED,
  ANALYTICS_EVENT_ACTION_REMOVED,
} from "@stadium/services/analytics/constants/default";
import { ANALYTICS_TARGET_FILTER } from "@constants/analytics/filter";

import styles from "./listing-header.module.scss";

const getTextFromNode = (node) => {
  if (!node) return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(getTextFromNode).join("");
  if (node?.props?.children) return getTextFromNode(node.props.children);
  return "";
};

const getMinItemTypeFilterItems = (isSwagCreationShopFlow = false) => {
  return isSwagCreationShopFlow ? 1 : 2;
};

const getItemTypeSelectedIds = (selectedItemTypes, itemTypeFilterData = {}) => {
  const includesAllProducts = itemTypeFilterData.items?.some(
    (item) => item.id === ALL_PRODUCTS_FILTER.id,
  );
  const defaultSelectedItem = includesAllProducts ? [ALL_PRODUCTS_FILTER.id] : [];
  return selectedItemTypes?.length ? selectedItemTypes : defaultSelectedItem;
};

const priceRangesEqual = (a, b) => isEqual(a?.slice(0, 2), b?.slice(0, 2));

const FilterWrapper = ({ Component, childProps = {}, hideFilterKeys = [] }) => {
  const { isPersonalWorkspace } = useWorkspaceContext();
  const { isSwagCreationShopFlow } = useCreateSwagContext();
  const { storeNumber } = useStoreContext();

  const withAnalytics = useWithAnalytics();
  const shopAnalyticsContext = useShopAnalyticsContext();

  const { query } = useRouter();
  const {
    currentDietaryRestrictionsIds,
    currentCountryIds,
    priceRanges,
    requiredDietaryRestrictionIds,
    colorOptionValues,
    currentColorOptionValueIds,
    sizeOptionValues,
    currentSizeOptionValueIds,
    setDietaryRestrictions,
    setDietaryRestrictionIds,
    setCountryIds,
    setPriceRangeIds,
    setColorOptionValueIds,
    setSizeOptionValueIds,
    selectedPriceRangeIds,
    isFetchingProducts,
    currentBrandIds,
    brandValues,
    setBrandValueIds,
    bulkProductsCount,
    selectedItemFeatures,
    onDemandProductsCount,
    coreOnDemandProductsCount,
    itemFeatures,
    setItemFeaturesFilter,
    onDemandModalShown,
    setOnDemandModalShown,
    volumeDiscountProductsCount,
    printTypeValues,
    currentDecorationMethods,
    setDecorationMethodsFilter,
    selectedItemTypes,
    setSelectedItemTypes,
    designedItemTypes,
    setSelectedDesignedTypes,
    countryIdValues,
  } = useContext(ProductListingContext);
  const isBundleShop = useSelector(getIsBundleShop);
  const isLockerShop = useSelector(getIsLockerShop);
  const isPricelessShop = useSelector(getIsPricelessShop);
  const appliedFiltersCount = useMemo(() => {
    return (
      currentDietaryRestrictionsIds.length +
      currentCountryIds.length +
      currentColorOptionValueIds.length +
      currentSizeOptionValueIds.length +
      currentBrandIds.length +
      selectedItemFeatures.length +
      selectedItemTypes.length +
      currentDecorationMethods.length +
      (selectedPriceRangeIds?.[1] ? 1 : 0)
    );
  }, [
    currentDietaryRestrictionsIds,
    currentCountryIds,
    currentColorOptionValueIds,
    currentSizeOptionValueIds,
    currentBrandIds,
    selectedItemFeatures,
    selectedItemTypes,
    currentDecorationMethods,
    selectedPriceRangeIds,
  ]);

  const { category } = query;
  const isMyDesignedSwag = category === MY_DESIGNED_SWAG_LOCKER;

  const [selectedItemType, setSelectedItemType] = useState("");
  const items = useConfig(selectRestrictionTaxons);
  const priceGestureBaselineRef = useRef(null);

  const { openModal, renderModal } = useModal(ADD_ONDEMAND_MODAL, AddOndemandModal);
  const filteredItems = items.filter((item) => requiredDietaryRestrictionIds?.includes(+item.id));

  useEffect(() => setDietaryRestrictions(items), [items, setDietaryRestrictions]);

  const trackFilterEvent = useCallback(
    (key, id, isRemoving, isReplacing = false) => {
      if (storeNumber) {
        // Resolve filter name
        let filterName = "";
        const allFilters = [
          ...items,
          ...countryIdValues,
          ...brandValues,
          ...colorOptionValues,
          ...sizeOptionValues,
          ...itemFeatures,
          ...printTypeValues,
          ...WORKSPACE_SHARING_DESIGNED_FILTERS,
          ...FILTER_ITEM_TYPES,
          ALL_PRODUCTS_FILTER,
        ];

        const findName = (targetId) => {
          const found = allFilters.find((f) => String(f.id) === String(targetId));
          const name = found?.label || found?.name || targetId;
          return getTextFromNode(name);
        };

        if (Array.isArray(id)) {
          filterName = id.map(findName).join(", ");
        } else {
          filterName = findName(id);
        }

        const isApplying = !isRemoving;
        const isApplyingFilter = isApplying ? 1 : -1;
        const countOffset = isReplacing ? 0 : isApplyingFilter;
        withAnalytics(undefined, {
          track: true,
          target: ANALYTICS_TARGET_FILTER,
          action: isRemoving ? ANALYTICS_EVENT_ACTION_REMOVED : ANALYTICS_EVENT_ACTION_APPLIED,
          eventProperties: {
            ...shopAnalyticsContext,
            filter_type: key,
            filter_id: id,
            filter_name: filterName,
            filter_value: !isRemoving,
            active_filters_count: appliedFiltersCount + countOffset,
          },
        })();
      }
    },
    [
      storeNumber,
      withAnalytics,
      shopAnalyticsContext,
      appliedFiltersCount,
      items,
      countryIdValues,
      brandValues,
      colorOptionValues,
      sizeOptionValues,
      itemFeatures,
      printTypeValues,
    ],
  );

  const trackPriceRangeAnalytics = useCallback(
    (ids, baseline) => {
      if (!storeNumber) return;
      const wasApplying = !!baseline?.[1];
      const isApplying = !!ids?.[1];
      let countOffset = 0;
      if (isApplying && !wasApplying) {
        countOffset = 1;
      } else if (!isApplying && wasApplying) {
        countOffset = -1;
      }

      withAnalytics(undefined, {
        track: true,
        target: ANALYTICS_TARGET_FILTER,
        action: isApplying ? ANALYTICS_EVENT_ACTION_APPLIED : ANALYTICS_EVENT_ACTION_REMOVED,
        eventProperties: {
          ...shopAnalyticsContext,
          filter_type: PRICE_FILTER_KEY,
          filter_value: ids,
          active_filters_count: appliedFiltersCount + countOffset,
        },
      })();
    },
    [storeNumber, withAnalytics, shopAnalyticsContext, appliedFiltersCount],
  );

  const handlePriceGestureStart = useCallback(() => {
    const min = priceRanges?.min || 0;
    const max = priceRanges?.max || 0;
    const currentVal = Array.isArray(selectedPriceRangeIds)
      ? [...selectedPriceRangeIds]
      : selectedPriceRangeIds;

    priceGestureBaselineRef.current =
      !currentVal || priceRangesEqual(currentVal, INIT_PRICE_RANGE_IDS) ? [min, max] : currentVal;
  }, [selectedPriceRangeIds, priceRanges]);

  const handlePriceLive = useCallback(
    (ids) => {
      setPriceRangeIds(ids);
    },
    [setPriceRangeIds],
  );

  const handlePriceSliderComplete = useCallback(
    (ids) => {
      const baseline = priceGestureBaselineRef.current;
      if (baseline && priceRangesEqual(ids, baseline)) {
        priceGestureBaselineRef.current = null;
        return;
      }
      trackPriceRangeAnalytics(ids, baseline);
      priceGestureBaselineRef.current = null;
    },
    [trackPriceRangeAnalytics],
  );

  const handlePriceCommitExplicit = useCallback(
    (ids, baseline) => {
      if (baseline && priceRangesEqual(ids, baseline)) {
        priceGestureBaselineRef.current = null;
        return;
      }
      trackPriceRangeAnalytics(ids, baseline);
      priceGestureBaselineRef.current = null;
    },
    [trackPriceRangeAnalytics],
  );

  const onChange = ({ key, id }) => {
    let mappings;
    switch (key) {
      case COUNTRY_FILTER_KEY:
        mappings = {
          selectedIds: currentCountryIds,
          setSelectedIds: setCountryIds,
        };
        break;
      case DIETARY_RESTRICTIONS_FILTER_KEY:
        mappings = {
          selectedIds: currentDietaryRestrictionsIds,
          setSelectedIds: setDietaryRestrictionIds,
        };
        break;
      case BRAND_FILTER_KEY:
        mappings = {
          selectedIds: currentBrandIds,
          setSelectedIds: setBrandValueIds,
        };
        break;
      case COLOR_FILTER_KEY:
        mappings = {
          selectedIds: currentColorOptionValueIds,
          setSelectedIds: setColorOptionValueIds,
        };
        break;
      case SIZE_FILTER_KEY:
        mappings = {
          selectedIds: currentSizeOptionValueIds,
          setSelectedIds: setSizeOptionValueIds,
        };
        break;
      case ITEM_FEATURES_FILTER_KEY:
        mappings = {
          selectedIds: selectedItemFeatures,
          setSelectedIds: setItemFeaturesFilter,
        };
        break;
      case ITEM_TYPE_FILTER_KEY:
        mappings = {
          selectedIds: selectedItemTypes,
          setSelectedIds: setSelectedItemTypes,
        };
        break;
      case DECORATION_METHOD_KEY:
        mappings = {
          selectedIds: currentDecorationMethods,
          setSelectedIds: setDecorationMethodsFilter,
        };
        break;
      case DESIGNED_TYPE_FILTER_KEY:
        mappings = {
          selectedIds: designedItemTypes,
          setSelectedIds: setSelectedDesignedTypes,
        };
        break;
    }

    if (mappings) {
      const { selectedIds, setSelectedIds } = mappings;
      const isRemoving = selectedIds.includes(id);

      if (isRemoving) {
        setSelectedIds(selectedIds.filter((l) => l !== id));
      } else {
        setSelectedIds([...selectedIds, id]);
      }

      trackFilterEvent(key, id, isRemoving);
    }
  };

  let data = [
    ...(isSwagCreationShopFlow
      ? [
          {
            label: "availability by country",
            items: countryIdValues,
            key: COUNTRY_FILTER_KEY,
            onChange,
            selectedIds: currentCountryIds,
          },
        ]
      : []),
    ...(!isPersonalWorkspace && (isBundleShop || (isLockerShop && isMyDesignedSwag))
      ? [
          {
            ...CUSTOM_FILTERS.workspaceSharing,
            onChange: (args) => {
              trackFilterEvent(
                DESIGNED_TYPE_FILTER_KEY,
                args.id,
                false,
                designedItemTypes.length > 0,
              );
              setSelectedDesignedTypes([args.id]);
            },
            selectedIds: designedItemTypes.length
              ? designedItemTypes
              : [ALL_DESIGNED_PRODUCTS_FILTER.id],
          },
        ]
      : []),
    {
      label: "Dietary Restrictions",
      items: filteredItems,
      key: DIETARY_RESTRICTIONS_FILTER_KEY,
      onChange,
      selectedIds: currentDietaryRestrictionsIds,
    },
    {
      label: "Brands",
      items: brandValues,
      key: BRAND_FILTER_KEY,
      onChange,
      selectedIds: currentBrandIds,
    },
    {
      label: COLOR_FILTER_KEY,
      items: colorOptionValues,
      key: "color",
      onChange,
      selectedIds: currentColorOptionValueIds,
    },
    {
      label: "Other",
      items: sizeOptionValues,
      key: SIZE_FILTER_KEY,
      onChange,
      selectedIds: currentSizeOptionValueIds,
    },
  ];

  const priceData = {
    label: PRICE_LABEL_TEXT,
    items: priceRanges,
    key: PRICE_FILTER_KEY,
    onChange: handlePriceLive,
    onBeforeChange: handlePriceGestureStart,
    onAfterChange: handlePriceSliderComplete,
    onCommit: handlePriceCommitExplicit,
    selectedIds: selectedPriceRangeIds,
  };

  if (printTypeValues?.length) {
    data.push({
      label: "Decoration Method",
      items: printTypeValues,
      key: DECORATION_METHOD_KEY,
      onChange,
      selectedIds: currentDecorationMethods,
    });
  }

  if (itemFeatures?.length) {
    data.push({
      label: "Attributes",
      items: itemFeatures,
      key: ITEM_FEATURES_FILTER_KEY,
      onChange,
      selectedIds: selectedItemFeatures,
    });
  }

  const effectiveHideFilterKeys = isSwagCreationShopFlow
    ? [...hideFilterKeys, COLOR_FILTER_KEY]
    : hideFilterKeys;
  data = data.filter((obj) => !effectiveHideFilterKeys.includes(obj.key));
  const itemTypeFilterEnabled = bulkProductsCount || onDemandProductsCount;

  const showOndemandModal = getShowOndemandModal({
    onDemandModalShown,
    itemType: selectedItemTypes[0],
    isBundleShop,
  });

  if (isBundleShop || itemTypeFilterEnabled) {
    const itemTypeFilterData = CUSTOM_FILTERS.itemType({
      isBundleShop,
      bulkProductsCount,
      onDemandProductsCount,
      volumeDiscountProductsCount,
      coreOnDemandProductsCount,
      isSwagCreationShopFlow,
    });
    const minItemFilterCount = getMinItemTypeFilterItems(isSwagCreationShopFlow);
    if (itemTypeFilterData.items.length >= minItemFilterCount) {
      data.push({
        ...itemTypeFilterData,
        onChange: ({ id, childIds, inputType }) => {
          /* Opening modal to ask the user whether to change the Pre-production filter or not.
          This modal will only be displayed the first time, and the user's preference will be stored in session storage. */
          if (showOndemandModal && id != LOCKER_INVENTORY_FILTER_KEY) {
            setOnDemandModalShown(true);
            setSelectedItemType(id);
            return openModal();
          }
          if (inputType === "checkbox") {
            return onChange({ key: ITEM_TYPE_FILTER_KEY, id });
          }
          if (childIds?.length) {
            trackFilterEvent(ITEM_TYPE_FILTER_KEY, childIds, false, selectedItemTypes.length > 0);
            return setSelectedItemTypes(childIds);
          }
          trackFilterEvent(ITEM_TYPE_FILTER_KEY, id, false, selectedItemTypes.length > 0);
          setSelectedItemTypes([id]);
        },
        selectedIds: getItemTypeSelectedIds(selectedItemTypes, itemTypeFilterData),
      });
    }
  }

  const sortedData = SORTED_FILTER_ITEMS.map((key) =>
    key === PRICE_FILTER_KEY && !isPricelessShop
      ? priceData
      : data.find(({ key: filterKey }) => filterKey === key),
  ).filter((elmt) => !isEmpty(elmt?.items));

  return (
    <>
      <Component
        isFetchingProducts={isFetchingProducts}
        data={sortedData}
        priceData={priceData}
        {...childProps}
      />
      {renderModal(
        {
          classes: {
            rootClass: styles["primaryModal"],
            dialogClass: styles["modalDialog"],
          },
          data: {
            onClick: () =>
              setSelectedItemTypes(
                selectedItemType == ALL_PRODUCTS_FILTER.id ? [] : [selectedItemType],
              ),
          },
        },
        false,
      )}
    </>
  );
};

export default FilterWrapper;
